/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
// NOTE: DO NOT INCLUDE SECRETS HERE, AS THIS IS SENT TO BROWSER

// TODO: Get rid of UprightEnvironment, and provide
// each derived configuration as a separate variable instead.
export enum UprightEnvironment {
  AWS_PRODUCTION = 'production',
  AWS_STAGING = 'staging',
  AWS_PLAYGROUND = 'aws-playground',
  AWS_WAFFLE = 'aws-waffle',
  AWS_MANGO = 'aws-mango',
  AWS_BISCUIT = 'aws-biscuit',
  AWS_TRUFFLE = 'aws-truffle',
  // Partial-cloud "hybrid" dev mode: upserve/upwork/vite run on the developer's
  // laptop and connect to the per-developer Fargate services + shared dev
  // services + per-developer RDS. All hybrid devs share this env tag — DDP/S3
  // keys are namespaced by it, so two devs running DDP simultaneously can
  // collide on the same shared keys (rare in practice; documented).
  AWS_HYBRID = 'aws-hybrid',
  DEVELOPMENT = 'development',
  TEST = 'test',
}

const awsEnvironments = [
  UprightEnvironment.AWS_PRODUCTION,
  UprightEnvironment.AWS_STAGING,
  UprightEnvironment.AWS_PLAYGROUND,
  UprightEnvironment.AWS_WAFFLE,
  UprightEnvironment.AWS_MANGO,
  UprightEnvironment.AWS_BISCUIT,
  UprightEnvironment.AWS_TRUFFLE,
  UprightEnvironment.AWS_HYBRID,
] as const;

export type AwsEnvironment = (typeof awsEnvironments)[number];

export function getHostNamePrefix(env: UprightEnvironment) {
  return `${env}-`;
}

/**
 * Get address for the elastic search instance
 * that contains the English Wikipedia articles database
 */
export function getElasticSearchWikiAddress() {
  const environment = getEnvironment();
  if (process.env.ELASTIC_SEARCH_WIKI) {
    return process.env.ELASTIC_SEARCH_WIKI;
  }
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- only TEST has a special address; all other environments fall back to the default
  switch (environment) {
    case UprightEnvironment.TEST:
      return 'localhost:9200';
    default:
      return 'localhost:9201';
  }
}

/**
 * Get address for the elastic search instance
 * that contains the CORE articles database
 */
export function getElasticSearchCoreAddress() {
  // const environment = getEnvironment();
  if (process.env.ELASTIC_SEARCH_CORE) {
    return process.env.ELASTIC_SEARCH_CORE;
  }
  return 'localhost:9200';
}

/**
 * Get address for the elastic search instance
 * that contains the CORE articles database
 */
export function getElasticSearchCore2Address() {
  // const environment = getEnvironment();
  if (process.env.ELASTIC_SEARCH_CORE2) {
    return process.env.ELASTIC_SEARCH_CORE2;
  }
  throw Error(`ELASTIC_SEARCH_CORE2 must be defined`);
}

/**
 * Get address for the elastic search instance
 * that is used for the search index for Upright search
 */
export function getElasticSearchUprightAddress() {
  if (process.env.ELASTIC_SEARCH_UPRIGHT) {
    return process.env.ELASTIC_SEARCH_UPRIGHT.split(',');
  }
  return ['localhost:9200'];
}

export function getUpserveInternalBaseUrl() {
  if (process.env.UPSERVE_BASEURL_INTERNAL) {
    return process.env.UPSERVE_BASEURL_INTERNAL;
  }
  const env = getEnvironment();
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- only DEVELOPMENT and TEST have a localhost fallback; all AWS environments must define UPSERVE_BASEURL_INTERNAL
  switch (env) {
    case UprightEnvironment.DEVELOPMENT:
    case UprightEnvironment.TEST:
      return 'http://localhost:3001';
    default:
      throw Error(`UPSERVE_BASEURL_INTERNAL should be defined outside of development and test environments`);
  }
}

export function isTest(environment?: UprightEnvironment) {
  const resolvedEnv = environment || process.env.NODE_ENV || getEnvironment();
  return resolvedEnv === UprightEnvironment.TEST;
}

export function isDev(environment?: UprightEnvironment) {
  const resolvedEnv = environment || getEnvironment();
  return resolvedEnv === UprightEnvironment.DEVELOPMENT;
}

export function isProduction(environment?: UprightEnvironment) {
  const resolvedEnv = environment || getEnvironment();
  return resolvedEnv === UprightEnvironment.AWS_PRODUCTION;
}

export function isStaging(environment?: UprightEnvironment) {
  const resolvedEnv = environment || getEnvironment();
  return resolvedEnv === UprightEnvironment.AWS_STAGING;
}

export function isAwsDev(environment?: UprightEnvironment) {
  const resolvedEnv = environment || getEnvironment();
  return isAwsEnvironment(resolvedEnv) && !isProduction(resolvedEnv) && !isStaging(resolvedEnv);
}

export function isAwsEnvironment(environment?: UprightEnvironment): environment is AwsEnvironment {
  const resolvedEnv = environment || getEnvironment();
  return awsEnvironments.includes(resolvedEnv as AwsEnvironment);
}

export function getEnvironment(): UprightEnvironment {
  // UPRIGHT_ENV is used in UI deployment and may be production, staging or development
  // NODE_ENV is either production or development
  const env = process.env.UPRIGHT_ENV || process.env.NODE_ENV || UprightEnvironment.DEVELOPMENT;
  return env as UprightEnvironment;
}
